import { z } from 'zod';

import {
	preprocessEmptyStringAsNull,
	preprocessEmptyStringAsUndefined,
} from '@dealstack/shared/lib/common/common';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DayCountConvention,
	PeriodUnitType,
	SecurityInterestDefinitionPrincipal,
} from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const SecurityInterestDefinitionShape = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>
) => {
	return z
		.object({
			id: z.string().optional(),
			accrualPeriod: z.nativeEnum(PeriodUnitType),
			interestFloatingRate: z.preprocess(
				preprocessEmptyStringAsUndefined,
				z.number().nonnegative().max(1000)
			),
			deemedStartDate: z.preprocess(
				preprocessEmptyStringAsNull,
				z.string().datetime().optional().nullable()
			),
			compoundingPeriod: z.nativeEnum(PeriodUnitType).optional().nullable(),
			firstInterestDate: z.preprocess(
				preprocessEmptyStringAsNull,
				z.string().datetime().optional().nullable()
			),
			dayCountConvention: z.nativeEnum(DayCountConvention),
			principal: z.nativeEnum(SecurityInterestDefinitionPrincipal),
			principalValue: z.preprocess(
				preprocessEmptyStringAsUndefined,
				z.number().nonnegative().optional()
			),
		})
		.refine(
			(data) => {
				const firstInterestDate = data.firstInterestDate
					? new Date(data.firstInterestDate)
					: null;
				const deemedStartDate = data.deemedStartDate
					? new Date(data.deemedStartDate)
					: null;

				if (!deemedStartDate || !firstInterestDate) {
					return true;
				}

				return firstInterestDate >= deemedStartDate;
			},
			{
				message: 'First interest date needs to be after deemed start date',
				path: ['firstInterestDate'],
			}
		)
		.refine(
			(data) => {
				if (data.principal !== SecurityInterestDefinitionPrincipal.ISSUE_COST_PER_UNIT) {
					return data.principalValue !== undefined;
				}

				return true;
			},
			{
				message: tCapManage('securities.interest_definition.validation.principal.required'),
				path: ['principalValue'],
			}
		);
};

export type SecurityInterestDefinitionFormDto = z.infer<
	ReturnType<typeof SecurityInterestDefinitionShape>
>;
